import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import Dashboard from './pages/Dashboard';
import MapView from './pages/MapView';
import MapAuto from './pages/MapAuto';
import MapV3 from './pages/MapV3';
import SystemHealth from './pages/SystemHealth';
import Devices from './pages/Devices';
import IncompleteDevices from './pages/IncompleteDevices';
import Characteristics from './pages/Characteristics';
import DeviceTypes from './pages/DeviceTypes';
import Locations from './pages/Locations';
import ConnectionTypes from './pages/ConnectionTypes';
import Connections from './pages/Connections';
import DeviceDetail from './pages/DeviceDetail';
import Matrices from './pages/Matrices';
import ChangeRequests from './pages/ChangeRequests';
import Drift from './pages/Drift';
import Assets from './pages/Assets';
import Warehouse from './pages/Warehouse';
import Issues from './pages/Issues';
import Analytics from './pages/Analytics';
import SystemChecks from './pages/SystemChecks';
import Monitoring from './pages/Monitoring';
import Integrations from './pages/Integrations';
import NetworkMatrix from './pages/NetworkMatrix';
import AgentDownload from './pages/AgentDownload';
import Users from './pages/Users';
import Roles from './pages/Roles';
import Profile from './pages/Profile';
import Subnets from './pages/Subnets';
import AuditLogs from './pages/AuditLogs';
import SoftwareCatalog from './pages/SoftwareCatalog';
import SoftwareDetail from './pages/SoftwareDetail';
import DeviceUsers from './pages/DeviceUsers';
import DatabaseManagement from './pages/DatabaseManagement';
import MainLayout from './components/MainLayout';
import { AuthProvider, useAuth } from './context/AuthContext';
const MapV4 = React.lazy(() => import('./pages/MapV4'));

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  // Wait for the async auth check to finish before deciding. Otherwise a refresh
  // on a deep link (e.g. /devices/64) renders <Navigate to="/login"/> before the
  // token is verified → bounces to login → home. (Was: only checked `user`.)
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  return user ? children : <Navigate to="/login" />;
}

// Component to handle route restoration
function RouteRestorer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    // Restore route on page load when:
    // 1. User is authenticated
    // 2. Current location is '/' (root)
    // 3. No refresh state is present (not a manual navigation to dashboard)
    if (user && location.pathname === '/' && !location.state?.refresh) {
      // Restore the last route only ONCE per browser session (the first app load).
      // Otherwise every later visit to Главная — or a refresh while on it — would
      // "bounce" you back to the last page, which is surprising/annoying.
      if (sessionStorage.getItem('routeRestored')) return;
      sessionStorage.setItem('routeRestored', '1');
      const lastRoute = localStorage.getItem('lastRoute');
      // Only restore if lastRoute is not '/', '/login', or '/set-password'
      if (lastRoute && lastRoute !== '/' && lastRoute !== '/login' && lastRoute !== '/set-password') {
        navigate(lastRoute, { replace: true });
      }
    }
  }, [user, navigate]); // Run when user or navigate changes

  return null;
}

function DashboardWrapper() {
  const location = useLocation();
  // Create a unique key from location state to force remount
  const key = location.state?.refresh || 'default';
  return <Dashboard key={key} />;
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <RouteRestorer />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/" element={<PrivateRoute><MainLayout><DashboardWrapper /></MainLayout></PrivateRoute>} />
            <Route path="/map" element={<PrivateRoute><MainLayout><MapView /></MainLayout></PrivateRoute>} />
            <Route path="/map-auto" element={<PrivateRoute><MainLayout><MapAuto /></MainLayout></PrivateRoute>} />
            <Route path="/map3" element={<PrivateRoute><MainLayout><MapV3 /></MainLayout></PrivateRoute>} />
            <Route path="/map4" element={<PrivateRoute><MainLayout><React.Suspense fallback={null}><MapV4 /></React.Suspense></MainLayout></PrivateRoute>} />
            <Route path="/health" element={<PrivateRoute><MainLayout><SystemHealth /></MainLayout></PrivateRoute>} />
            <Route path="/devices" element={<PrivateRoute><MainLayout><Devices /></MainLayout></PrivateRoute>} />
            <Route path="/devices/:id" element={<PrivateRoute><MainLayout><DeviceDetail /></MainLayout></PrivateRoute>} />
            <Route path="/incomplete-devices" element={<PrivateRoute><MainLayout><IncompleteDevices /></MainLayout></PrivateRoute>} />
            <Route path="/characteristics" element={<PrivateRoute><MainLayout><Characteristics /></MainLayout></PrivateRoute>} />
            <Route path="/device-types" element={<PrivateRoute><MainLayout><DeviceTypes /></MainLayout></PrivateRoute>} />
            <Route path="/locations" element={<PrivateRoute><MainLayout><Locations /></MainLayout></PrivateRoute>} />
            <Route path="/connection-types" element={<PrivateRoute><MainLayout><ConnectionTypes /></MainLayout></PrivateRoute>} />
            <Route path="/connections" element={<PrivateRoute><MainLayout><Connections /></MainLayout></PrivateRoute>} />
            <Route path="/subnets" element={<PrivateRoute><MainLayout><Subnets /></MainLayout></PrivateRoute>} />
            <Route path="/matrices" element={<PrivateRoute><MainLayout><Matrices /></MainLayout></PrivateRoute>} />
            <Route path="/change-requests" element={<PrivateRoute><MainLayout><ChangeRequests /></MainLayout></PrivateRoute>} />
            <Route path="/drift" element={<PrivateRoute><MainLayout><Drift /></MainLayout></PrivateRoute>} />
            <Route path="/assets" element={<PrivateRoute><MainLayout><Assets /></MainLayout></PrivateRoute>} />
            <Route path="/warehouse" element={<PrivateRoute><MainLayout><Warehouse /></MainLayout></PrivateRoute>} />
            <Route path="/issues" element={<PrivateRoute><MainLayout><Issues /></MainLayout></PrivateRoute>} />
            <Route path="/analytics" element={<PrivateRoute><MainLayout><Analytics /></MainLayout></PrivateRoute>} />
            <Route path="/system-checks" element={<PrivateRoute><MainLayout><SystemChecks /></MainLayout></PrivateRoute>} />
            <Route path="/monitoring" element={<PrivateRoute><MainLayout><Monitoring /></MainLayout></PrivateRoute>} />
            <Route path="/integrations" element={<PrivateRoute><MainLayout><Integrations /></MainLayout></PrivateRoute>} />
            <Route path="/network-matrix" element={<PrivateRoute><MainLayout><NetworkMatrix /></MainLayout></PrivateRoute>} />
            <Route path="/agent-download" element={<PrivateRoute><MainLayout><AgentDownload /></MainLayout></PrivateRoute>} />
            <Route path="/audit-logs" element={<PrivateRoute><MainLayout><AuditLogs /></MainLayout></PrivateRoute>} />
            <Route path="/software-catalog" element={<PrivateRoute><MainLayout><SoftwareCatalog /></MainLayout></PrivateRoute>} />
            <Route path="/software-catalog/:id" element={<PrivateRoute><MainLayout><SoftwareDetail /></MainLayout></PrivateRoute>} />
            <Route path="/device-users" element={<PrivateRoute><MainLayout><DeviceUsers /></MainLayout></PrivateRoute>} />
            <Route path="/users" element={<PrivateRoute><MainLayout><Users /></MainLayout></PrivateRoute>} />
            <Route path="/roles" element={<PrivateRoute><MainLayout><Roles /></MainLayout></PrivateRoute>} />
            <Route path="/database" element={<PrivateRoute><MainLayout><DatabaseManagement /></MainLayout></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><MainLayout><Profile /></MainLayout></PrivateRoute>} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
